// ============================================================================
// OPERATION PROGRESS BAR - Component per mostrar el progrés d'operacions
// Mostra una barra de progrés flotant per cada operació activa
// ============================================================================

import React from 'react';
import { Banner, ProgressBar, Card, Text, InlineStack, BlockStack, Icon, Button } from '@shopify/polaris';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';

// Interface per a les operacions actives
export interface ActiveOperation {
  correlationId: string;
  operationType: string;
  status: 'started' | 'progress' | 'completed' | 'error';
  percentComplete: number;
  message: string;
  startTime: Date;
  endTime?: Date;
  itemsProcessed?: number;
  durationMs?: number;
  error?: string;
}

interface OperationProgressBarProps {
  operations: ActiveOperation[];
  onDismiss?: (correlationId: string) => void;
}

// Translate operation type to display name using i18n
const getOperationDisplayName = (t: (key: string) => string, operationType: string): string => {
  const key = `operations.names.${operationType}`;
  const translated = t(key);
  return translated === key ? operationType : translated;
};

// Format duration
const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
};

const OperationItem: React.FC<{
  operation: ActiveOperation;
  onDismiss?: () => void;
}> = ({ operation, onDismiss }) => {
  const { t } = useTranslation();
  const isActive = operation.status === 'started' || operation.status === 'progress';
  const isCompleted = operation.status === 'completed';
  const isError = operation.status === 'error';

  const statusIcon = isCompleted ? CheckCircleIcon : isError ? XCircleIcon : ClockIcon;
  const statusColor = isCompleted ? 'success' : isError ? 'critical' : 'info';

  const elapsedTime = operation.endTime
    ? formatDuration(operation.endTime.getTime() - operation.startTime.getTime())
    : formatDuration(Date.now() - operation.startTime.getTime());

  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Icon source={statusIcon} tone={statusColor} />
            <Text as="span" variant="headingSm">
              {getOperationDisplayName(t, operation.operationType)}
            </Text>
          </InlineStack>
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              {elapsedTime}
            </Text>
            {(isCompleted || isError) && onDismiss && (
              <Button size="micro" variant="plain" onClick={onDismiss}>
                ✕
              </Button>
            )}
          </InlineStack>
        </InlineStack>

        {isActive && (
          <ProgressBar 
            progress={operation.percentComplete} 
            size="small"
            tone={operation.percentComplete < 50 ? 'primary' : 'success'}
          />
        )}

        <InlineStack align="space-between" blockAlign="center">
          <Text as="span" variant="bodySm" tone="subdued">
            {operation.message}
          </Text>
          {isActive && (
            <Text as="span" variant="bodySm" fontWeight="semibold">
              {operation.percentComplete}%
            </Text>
          )}
          {isCompleted && operation.itemsProcessed !== undefined && (
            <Text as="span" variant="bodySm" tone="success">
              {t('operations.itemsProcessed', { count: operation.itemsProcessed })}
            </Text>
          )}
        </InlineStack>

        {isError && operation.error && (
          <Banner tone="critical" hideIcon>
            {operation.error}
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
};

export const OperationProgressBar: React.FC<OperationProgressBarProps> = ({
  operations,
  onDismiss,
}) => {
  // Debug log
  if (operations.length > 0) {
    console.log('[ProgressBar] Rendering operations:', operations);
  }
  
  if (operations.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: '400px',
      maxHeight: '60vh',
      overflowY: 'auto',
      zIndex: 1000,
    }}>
      <BlockStack gap="200">
        {operations.map(operation => (
          <OperationItem
            key={operation.correlationId}
            operation={operation}
            onDismiss={onDismiss ? () => onDismiss(operation.correlationId) : undefined}
          />
        ))}
      </BlockStack>
    </div>
  );
};

// Versió simplificada per mostrar en línia
export const InlineOperationProgress: React.FC<{
  operation: ActiveOperation;
}> = ({ operation }) => {
  const { t } = useTranslation();
  const isActive = operation.status === 'started' || operation.status === 'progress';
  
  if (!isActive) return null;

  return (
    <BlockStack gap="100">
      <InlineStack align="space-between">
        <Text as="span" variant="bodySm">
          {getOperationDisplayName(t, operation.operationType)}
        </Text>
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {operation.percentComplete}%
        </Text>
      </InlineStack>
      <ProgressBar 
        progress={operation.percentComplete} 
        size="small"
      />
      <Text as="span" variant="bodySm" tone="subdued">
        {operation.message}
      </Text>
    </BlockStack>
  );
};

export default OperationProgressBar;
